/** Bascule photos + tailles/stock ensemble au survol/clic d'une pastille couleur —
 * sur la carte boutique (`.product-card`) comme sur la fiche produit (`.produit`).
 * Chaque élément lié à une couleur (image, pastille, panneau tailles/galerie) porte
 * le même `data-color-id` ; le survol prévisualise, le clic fixe le choix. */
export function initColorSwitch(): void {
  document.querySelectorAll<HTMLElement>('[data-color-root]').forEach((scope) => {
    const swatches = scope.querySelectorAll<HTMLElement>('.color-swatch');
    if (swatches.length < 2) return;

    const images = scope.querySelectorAll<HTMLElement>('.color-image');
    const panels = scope.querySelectorAll<HTMLElement>('.color-panel');
    let selected = swatches[0]?.dataset.colorId ?? '';

    function apply(colorId: string): void {
      images.forEach((img) => img.classList.toggle('is-active', img.dataset.colorId === colorId));
      panels.forEach((panel) => {
        const match = panel.dataset.colorId === colorId;
        panel.hidden = !match;
        if (match) panel.querySelector('.product-card__sizes')?.dispatchEvent(new Event('scroll'));
      });
      swatches.forEach((s) => s.classList.toggle('is-active', s.dataset.colorId === colorId));
    }

    swatches.forEach((swatch) => {
      const id = swatch.dataset.colorId ?? '';
      swatch.addEventListener('mouseenter', () => apply(id));
      swatch.addEventListener('mouseleave', () => apply(selected));
      swatch.addEventListener('click', () => {
        selected = id;
        apply(id);
      });
    });
  });
}

/** Rend cliquables les puces de taille des cartes produit (`.product-card__size`,
 * dans `.product-card__sizes-wrap.color-panel`) — sélectionne automatiquement la
 * première taille disponible de la couleur active, nécessaire pour l'ajout rapide
 * au panier (voir `client/cart/cartActions.ts`). Pas de sélection sur les tailles
 * en rupture de stock (`--unavailable`). */
export function initSizeSelect(): void {
  document.querySelectorAll<HTMLElement>('.product-card__sizes-wrap').forEach((wrap) => {
    const sizes = Array.from(wrap.querySelectorAll<HTMLElement>('.product-card__size:not(.product-card__size--unavailable)'));
    if (sizes.length === 0) return;

    function select(target: HTMLElement): void {
      sizes.forEach((s) => s.classList.toggle('is-selected', s === target));
    }

    sizes.forEach((size) => {
      size.addEventListener('click', () => select(size));
    });

    select(sizes[0]!);
  });
}

/** Gère l'apparition et le défilement des boutons prev/next d'une liste de tailles
 * horizontale trop longue pour tenir dans la carte produit (`.product-card`). */
export function initSizesScroll(): void {
  document.querySelectorAll<HTMLElement>('.product-card__sizes-wrap').forEach((wrap) => {
    const list = wrap.querySelector<HTMLElement>('.product-card__sizes');
    const prev = wrap.querySelector<HTMLButtonElement>('.sizes-scroll-btn--prev');
    const next = wrap.querySelector<HTMLButtonElement>('.sizes-scroll-btn--next');
    if (!list || !prev || !next) return;

    const STEP = 72;

    function sync(): void {
      const overflow = list!.scrollWidth > list!.clientWidth + 1;
      prev!.classList.toggle('is-visible', overflow && list!.scrollLeft > 0);
      next!.classList.toggle('is-visible', overflow && list!.scrollLeft < list!.scrollWidth - list!.clientWidth - 1);
    }

    prev.addEventListener('click', () => { list.scrollBy({ left: -STEP, behavior: 'smooth' }); });
    next.addEventListener('click', () => { list.scrollBy({ left: STEP, behavior: 'smooth' }); });
    list.addEventListener('scroll', sync, { passive: true });
    sync();
    setTimeout(sync, 150);
  });
}
